import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Clock, Trophy, ArrowLeft } from 'lucide-react';
import { Exercise, Quest, QUEST_CORRECTION_THRESHOLD } from '@unity-learning/shared';
import { getExercise, getQuest, type QuestWithParticipation } from '../services/api';
import { useUser } from '../hooks/useUser';
import ExercisePage from './ExercisePage';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

interface QuestExercisePageProps {
  mode?: 'new' | 'review';
}

const QuestExercisePage: React.FC<QuestExercisePageProps> = ({ mode = 'new' }) => {
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [quest, setQuest] = useState<Quest | null>(null);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userParticipation, setUserParticipation] = useState<{
    participated: boolean;
    completed: boolean;
    bestScore: number;
  } | null>(null);

  useEffect(() => {
    if (questId && user) {
      fetchQuestData();
    }
  }, [questId, user]);

  const fetchQuestData = async () => {
    try {
      setIsLoading(true);

      // Fetch quest details
      const questData: QuestWithParticipation = await getQuest(questId!);
      setQuest(questData);
      setUserParticipation(questData.userParticipation || null);

      // Fetch exercise details
      setExercise(await getExercise(questData.exerciseId));
      setError(null);
    } catch (err) {
      console.error('Error fetching quest data:', err);
      setError((err as Error).message);
      toast.error('Error loading quest: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Quest-specific correction button logic
  const shouldShowCorrectionButton = (score: number): boolean => {
    return score >= QUEST_CORRECTION_THRESHOLD;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la quête...</p>
        </div>
      </div>
    );
  }

  if (error || !quest || !exercise) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quête non trouvée</h2>
          <p className="text-gray-600 mb-4">{error || 'Cette quête n\'existe pas ou n\'est plus active.'}</p>
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Quest Header */}
      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 dark:from-purple-900 dark:to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Star className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Quête</h1>
                <div className="text-amber-100 dark:text-gray-300">
                  <ReactMarkdown components={{
                    p: ({ children }) => <span>{children}</span>,
                    strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                    code: ({ children }) => <code className="bg-white/20 px-1 rounded">{children}</code>
                  }}>
                    {quest.title}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Quest Status */}
              <div className="flex items-center space-x-2 bg-white bg-opacity-30 dark:bg-gray-800 dark:bg-opacity-50 rounded-full px-3 py-1.5 border border-white border-opacity-20 dark:border-gray-600">
                {quest.status === 'active' ? (
                  <>
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Active</span>
                  </>
                ) : quest.status === 'scheduled' ? (
                  <>
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Programmée</span>
                  </>
                ) : (
                  <>
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm font-medium">Terminée</span>
                  </>
                )}
              </div>

              {/* User Progress */}
              {userParticipation && (
                <div className="flex items-center space-x-2 bg-white bg-opacity-30 dark:bg-gray-800 dark:bg-opacity-50 rounded-full px-3 py-1.5 border border-white border-opacity-20 dark:border-gray-600">
                  {userParticipation.completed ? (
                    <>
                      <Trophy className="w-4 h-4 text-yellow-200 dark:text-yellow-400" />
                      <span className="text-sm font-medium">
                        Complété ({userParticipation.bestScore}%)
                      </span>
                    </>
                  ) : userParticipation.participated ? (
                    <>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        En cours ({userParticipation.bestScore}%)
                      </span>
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4" />
                      <span className="text-sm font-medium">Nouveau</span>
                    </>
                  )}
                </div>
              )}

              <button
                onClick={handleBackToHome}
                className="inline-flex items-center px-3 py-1 bg-white bg-opacity-20 dark:bg-gray-800 dark:bg-opacity-50 rounded-md hover:bg-opacity-30 dark:hover:bg-opacity-70 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Accueil
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quest Info Banner */}
      <div className="bg-amber-50 dark:bg-gray-800 border-b border-amber-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4 text-amber-700 dark:text-amber-400">
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4" />
                <span className="font-medium">Quête spéciale</span>
              </div>
              <div>
                <span>Score requis pour voir la correction: {QUEST_CORRECTION_THRESHOLD}%</span>
              </div>
            </div>

            {userParticipation?.participated && quest.status === 'active' && (
              <div className="text-amber-700 dark:text-amber-400">
                Vous pouvez refaire la quête pour améliorer votre score
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exercise Content with Quest Context */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <QuestExerciseContent
          exercise={exercise}
          quest={quest}
          mode={mode}
          shouldShowCorrectionButton={shouldShowCorrectionButton}
          onQuestCompleted={fetchQuestData}
          navigate={navigate}
        />
      </div>
    </div>
  );
};

// Separate component for exercise content to avoid circular imports
interface QuestExerciseContentProps {
  exercise: Exercise;
  quest: Quest;
  mode?: 'new' | 'review';
  shouldShowCorrectionButton: (score: number) => boolean;
  onQuestCompleted: () => void;
  navigate: (path: string) => void;
}

const QuestExerciseContent: React.FC<QuestExerciseContentProps> = ({
  exercise,
  quest,
  mode,
  shouldShowCorrectionButton,
  onQuestCompleted,
  navigate
}) => {
  // We'll create a wrapper that provides quest context to the regular ExercisePage
  // This is a simplified approach - in a real implementation, you might want to
  // modify the ExercisePage directly or create a more sophisticated wrapper

  return (
    <div className="quest-exercise-wrapper">
      {/* Quest-specific styling and behavior can be added here */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 border-amber-200 dark:border-purple-500/30">
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Star className="w-5 h-5 text-amber-500" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                <ReactMarkdown components={{
                  p: ({ children }) => <span>{children}</span>,
                  strong: ({ children }) => <strong className="font-bold text-amber-600 dark:text-amber-400">{children}</strong>,
                  code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{children}</code>
                }}>
                  {exercise.title}
                </ReactMarkdown>
              </h2>
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              <ReactMarkdown components={{
                strong: ({ children }) => <strong className="font-bold text-gray-900 dark:text-white">{children}</strong>,
                code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>
              }}>
                {exercise.description}
              </ReactMarkdown>
            </div>
          </div>

          {/* Note: This is a simplified version. In a full implementation, 
               you would integrate the full ExercisePage functionality here 
               with quest-specific correction button logic */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Star className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-blue-800 dark:text-blue-300 font-medium">Exercice de quête</p>
                <p className="text-blue-700 dark:text-blue-400 text-sm mt-1">
                  Cet exercice fait partie de la quête "<ReactMarkdown components={{
                    p: ({ children }) => <span className="inline">{children}</span>,
                    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                    code: ({ children }) => <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{children}</code>
                  }}>{quest.title}</ReactMarkdown>".
                  {quest.status === 'active'
                    ? ' Votre progression sera automatiquement enregistrée pour la quête.'
                    : ' Cette quête n\'est pas active : vous pouvez faire l\'exercice, mais vos copies ne comptent pas pour la quête.'}
                </p>
              </div>
            </div>
          </div>

          {/* Access to Normal Exercise */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
            <button
              onClick={() => navigate(`/exercise/${exercise.id}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Ouvrir l'exercice normal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestExercisePage;
